if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item']
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.bindLayoutRefreshEvents();
        if (this.dataset.blDeferInit === 'true') return;
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      disconnectedCallback() {
        this.unbindLayoutRefreshEvents();
      }

      bindLayoutRefreshEvents() {
        if (this._boundRefreshSliderLayout) return;

        this._boundRefreshSliderLayout = this.refreshSliderLayout.bind(this);
        document.addEventListener('drawer:handleAfterShow', this._boundRefreshSliderLayout);
        document.addEventListener('drawer:handleAfterHide', this._boundRefreshSliderLayout);
        window.addEventListener('pageshow', this._boundRefreshSliderLayout);
      }

      unbindLayoutRefreshEvents() {
        if (!this._boundRefreshSliderLayout) return;

        document.removeEventListener('drawer:handleAfterShow', this._boundRefreshSliderLayout);
        document.removeEventListener('drawer:handleAfterHide', this._boundRefreshSliderLayout);
        window.removeEventListener('pageshow', this._boundRefreshSliderLayout);
        this._boundRefreshSliderLayout = null;
      }

      refreshSliderLayout() {
        if (!this.isConnected) return;

        const refresh = () => {
          const slider = this.sliderInstance?.slider;
          const thumbs = this.thumbsInstance?.slider;

          if (slider && !slider.destroyed) {
            slider.update?.();
            slider.updateAutoHeight?.(0);
          }

          if (thumbs && !thumbs.destroyed) {
            thumbs.update?.();
          }
        };

        requestAnimationFrame(() => {
          refresh();
          requestAnimationFrame(refresh);
        });
        window.setTimeout(refresh, 320);
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.mobilePagination = this.dataset.mobilePagination || 'fraction';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: 1,
          centeredSlides: false,
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev')
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: this.mobilePagination === 'bullets' ? 'bullets' : 'fraction'
          },
          threshold: 2
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal'
            }
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          }
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap
              }
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5
              },
              1536: {
                slidesPerView: 7
              }
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }

        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      // BL 2026-08-26: el zoom se construia sobre TODAS las .product__media-item, incluidas las
      // suplementarias que el server manda con `hidden` (las slides que el selector v3 necesita
      // vivas para poder hacer el swap de combo). Resultado: la rejilla ensenaba 4 imagenes y el
      // lightbox recorria las 10-14. Ademas el conjunto vivo CAMBIA en runtime -- filterGallery
      // monta/desmonta slides al cambiar de combo, y product-info reemplaza la galeria entera tras
      // un cambio de Breite y vuelve a llamar init() sobre HTML sin recortar -- asi que la lista no
      // puede quedarse congelada en init(): se recalcula en cada apertura.
      getZoomMediaItems() {
        const visible = [
          ...this.querySelectorAll(
            '.product__media-item:not(.swiper-slide-duplicate):not([hidden]):not(.bl-v3-gallery-supplemental)'
          )
        ];
        if (visible.length) return visible;

        // Red de seguridad: si algo dejase marcadas como ocultas TODAS las slides, es preferible
        // abrir el zoom con la galeria entera que abrirlo vacio.
        return [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
      }

      buildZoomDataSource(mediaItems) {
        return mediaItems.map((media) => {
          const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

          let source = {
            id: mediaIndex,
            mediaType: mediaType
          };

          switch (mediaType) {
            case 'model':
            case 'video':
            case 'external_video':
              const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

              source = {
                ...source,
                html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`
              };
              break;
            default: // Image.
              source = {
                ...source,
                src: src,
                width: pswpWidth,
                height: pswpHeight
              };
              break;
          }

          return source;
        });
      }

      initImageZoom() {
        this.zoomMediaItems = this.getZoomMediaItems();

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: this.buildZoomDataSource(this.zoomMediaItems),
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          // Don't restore focus to the opener thumbnail on close. The opener lives
          // inside the slide it was opened from, so when the tab is re-activated the
          // browser re-focuses (and scrolls) that element into view, hijacking the
          // swiper back to the opening index. Keeping focus off it prevents the jump.
          returnFocus: false
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          return this.querySelector(`.product__media-item[data-media-index="${id}"] img`) || thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          const { currIndex } = this.lightbox?.pswp;
          const slider = this.sliderInstance?.slider;
          if (slider) {
            slider.slideTo(currIndex, 0, false);
          }
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox?.pswp;
          if (!this.sliderInstance && currIndex !== null) {
            // currIndex indexa el dataSource, que ahora es la lista VIVA (no el orden servidor),
            // asi que la slide de vuelta se resuelve por esa misma lista.
            const target = this.zoomMediaItems?.[currIndex];
            if (target) {
              requestAnimationFrame(() => {
                const rect = target.getBoundingClientRect();
                const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
                if (!inView) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              });
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              }
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              }
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            }
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType !== 'image') return;

            // El conjunto vivo cambia con cada swap del selector, y el indice del dataSource ya no
            // coincide con data-media-index (posicion servidor): se resuelve por data-media-id.
            this.zoomMediaItems = this.getZoomMediaItems();
            const slide = media.closest('.product__media-item') || media;
            const mediaId = String(slide.dataset.mediaId ?? media.dataset.mediaId ?? '');
            const index = this.zoomMediaItems.findIndex((item) => String(item.dataset.mediaId) === mediaId);

            this.lightbox.loadAndOpen(index < 0 ? 0 : index, this.buildZoomDataSource(this.zoomMediaItems));
          }
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image'
          };
        }

        if (!featuredMedia) return;

        const slider = this.sliderInstance?.slider;
        if (slider) {
          const slides = Array.from(slider.slides || []).filter(
            (slide) => !slide.classList?.contains('swiper-slide-duplicate')
          );

          let targetIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(featuredMedia.id));

          if (targetIndex < 0) {
            const positionIndex = Math.max(0, (featuredMedia.position || 1) - 1);
            if (positionIndex < slides.length) {
              targetIndex = positionIndex;
            }
          }

          if (targetIndex < 0) {
            targetIndex = 0;
          }

          slider.slideTo(targetIndex, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image'
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

      }
    }
  );
}
